
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { insertBroWithRank, rerollBroCode, upgradeToBrometheus } from '@/lib/broCode';
import { useUnifiedWallet } from '@/hooks/useUnifiedWallet';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';


export const useInitiationHandlers = (_mobileDetected: boolean = false) => {
  const { address, chainType } = useUnifiedWallet();
  const { webApp, isTelegram } = useTelegramWebApp();
  const [isLoading, setIsLoading] = useState(false);

  // Get authentication headers for API calls
  const getAuthHeaders = () => {
    const headers: { [key: string]: string } = {};
    if (webApp?.initData) {
      headers['x-telegram-data'] = webApp.initData;
    }
    return headers;
  };

  const handleConnectWallet = () => {
    // This will be handled by the UnifiedConnectWallet component
    console.log('Wallet connection initiated...');
  };

  const handleCipherPath = async (rank: string) => {
    if (!address) {
      toast.error('Please connect your wallet first');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`🎯 Processing Cipher path for rank: ${rank}, chain: ${chainType}`);
      
      // Check if we're in Telegram mini-app context
      const telegramUser = isTelegram && webApp?.initDataUnsafe?.user ? webApp.initDataUnsafe.user : null;
      
      if (telegramUser) {
        // Use complete-initiation edge function for mini-app users
        console.log(`🔄 Using complete-initiation edge function for Telegram user: ${telegramUser.id}`);
        
        const { data, error } = await supabase.functions.invoke('complete-initiation', {
          body: {
            telegramId: telegramUser.id,
            walletAddress: address.toLowerCase(),
            chainType: chainType,
            chosenPath: 'cipher',
            twitterHandle: null
          },
          headers: getAuthHeaders()
        });

        if (error) {
          console.error('❌ Error calling complete-initiation:', error);
          toast.error(`Failed to create BroCode: ${error.message}`);
          return;
        }

        if (!data?.success) {
          if (data?.error?.includes('already has a BroCode')) {
            toast.success(`Welcome back! Your BroCode: ${data.userData?.brocode}`);
            window.location.reload();
            return;
          }
          
          toast.error(`Failed to create BroCode: ${data?.error || 'Unknown error'}`);
          return;
        }
        
        console.log(`✅ Cipher initiation successful via edge function: ${data.userData?.brocode}`);
        toast.success(`Welcome to the Brotherhood! Your BroCode: ${data.userData?.brocode}`);
        
        // Refresh initiation state
        window.location.reload();
      } else {
        // Fallback to legacy system for web-only users
        console.log(`🔄 Using legacy insertBroWithRank for web user`);
        
        // Validate cipher ranks
        const validCipherRanks = ['Initiated Bro', 'Shadow Bro'];
        if (!validCipherRanks.includes(rank)) {
          throw new Error(`Invalid cipher rank: ${rank}`);
        }
        
        const result = await insertBroWithRank(address, rank, 'cipher');
        
        if (result.error) {
          toast.error(`Failed to create BroCode: ${result.error}`);
          return;
        }
        
        console.log(`✅ Cipher initiation successful via legacy system: ${result.brocode}`);
        toast.success(`Welcome to the Brotherhood! Your BroCode: ${result.brocode}`);
        
        // Refresh initiation state
        window.location.reload();
      }
      
    } catch (error) {
      console.error('❌ Error in cipher initiation:', error);
      toast.error('Failed to complete initiation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialBroPath = async () => {
    if (!address) {
      toast.error('Please connect your wallet first');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`🎯 Processing Social Bro path, chain: ${chainType}`);
      
      // Check if we're in Telegram mini-app context
      const telegramUser = isTelegram && webApp?.initDataUnsafe?.user ? webApp.initDataUnsafe.user : null;
      
      if (telegramUser) {
        // Use complete-initiation edge function for mini-app users
        console.log(`🔄 Using complete-initiation edge function for Telegram user: ${telegramUser.id}`);
        
        const { data, error } = await supabase.functions.invoke('complete-initiation', {
          body: {
            telegramId: telegramUser.id,
            walletAddress: address.toLowerCase(),
            chainType: chainType,
            chosenPath: 'social',
            twitterHandle: null
          },
          headers: getAuthHeaders()
        });

        if (error) {
          console.error('❌ Error calling complete-initiation:', error);
          toast.error(`Failed to create BroCode: ${error.message}`);
          return;
        }

        if (!data?.success) {
          if (data?.error?.includes('already has a BroCode')) {
            toast.success(`Welcome back! Your BroCode: ${data.userData?.brocode}`);
            window.location.reload();
            return;
          }
          
          toast.error(`Failed to create BroCode: ${data?.error || 'Unknown error'}`);
          return;
        }
        
        console.log(`✅ Social Bro initiation successful via edge function: ${data.userData?.brocode}`);
        toast.success(`Welcome Social Bro! Your BroCode: ${data.userData?.brocode}`);
        
        // Refresh initiation state
        window.location.reload();
      } else {
        // Fallback to legacy system for web-only users
        console.log(`🔄 Using legacy insertBroWithRank for web user`);
        
        const result = await insertBroWithRank(address, 'Social Bro', 'social');
        
        if (result.error) {
          toast.error(`Failed to create BroCode: ${result.error}`);
          return;
        }
        
        console.log(`✅ Social Bro initiation successful via legacy system: ${result.brocode}`);
        toast.success(`Welcome Social Bro! Your BroCode: ${result.brocode}`);
        
        // Refresh initiation state
        window.location.reload();
      }
      
    } catch (error) {
      console.error('❌ Error in social bro initiation:', error);
      toast.error('Failed to complete initiation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ PRESERVED: Reroll functionality still uses legacy system as intended
  const handleReroll = async (currentBroScore: number) => {
    if (!address) {
      toast.error('Please connect your wallet first');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🎲 Processing BroCode reroll (using legacy reroll system)');
      
      const result = await rerollBroCode(address, currentBroScore);
      
      if (result.error) {
        toast.error(`Reroll failed: ${result.error}`);
        return;
      }
      
      console.log(`✅ BroCode reroll successful: ${result.brocode}`);
      toast.success(`New BroCode generated: ${result.brocode}`);
      
      // Refresh to show new BroCode
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Error in BroCode reroll:', error);
      toast.error('Failed to reroll BroCode. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ PRESERVED: XP reroll uses legacy reroll system
  const handleRerollWithXp = async (address: string): Promise<{ success: boolean; brocode?: string }> => {
    try {
      const { data: userData } = await supabase
        .from('initiated_bros')
        .select('bro_score')
        .eq('wallet_address', address.toLowerCase())
        .single();

      if (!userData || (userData.bro_score ?? 0) < 50) {
        toast.error('Insufficient BroScore for reroll (need 50)');
        return { success: false };
      }

      console.log('🎲 Processing XP reroll (using legacy reroll system)');
      const result = await rerollBroCode(address, userData.bro_score ?? 0, 50);
      
      if (result.error) {
        toast.error(result.error);
        return { success: false };
      }

      return { success: true, brocode: result.brocode };
    } catch (error) {
      console.error('Error in XP reroll:', error);
      toast.error('Failed to reroll with XP');
      return { success: false };
    }
  };

  const handleRerollWithTokens = async (_address: string): Promise<{ success: boolean; brocode?: string }> => {
    try {
      // Token-based reroll logic will be implemented based on chainType
      toast.info(`Token reroll for ${chainType} not yet implemented`);
      return { success: false };
    } catch (error) {
      console.error('Error in token reroll:', error);
      return { success: false };
    }
  };

  const handleRerollWithLiquidity = async (_address: string): Promise<{ success: boolean; brocode?: string }> => {
    try {
      // Liquidity-based reroll logic will be implemented based on chainType
      toast.info(`Liquidity reroll for ${chainType} not yet implemented`);
      return { success: false };
    } catch (error) {
      console.error('Error in liquidity reroll:', error);
      return { success: false };
    }
  };

  // ✅ PRESERVED: BROMETHEUS upgrade uses legacy upgrade system
  const handleBrometheusUpgrade = async (
    address: string, 
    _useXP: boolean = false, 
    _useLiquidity: boolean = false
  ): Promise<{ success: boolean; brocode?: string }> => {
    try {
      const { data: userData } = await supabase
        .from('initiated_bros')
        .select('bro_score, is_brometheus')
        .eq('wallet_address', address.toLowerCase())
        .single();

      if (!userData) {
        toast.error('User not found');
        return { success: false };
      }

      if (userData.is_brometheus) {
        toast.error('Already BROMETHEUS');
        return { success: false };
      }

      const upgradeCost = 500;
      if ((userData.bro_score ?? 0) < upgradeCost) {
        toast.error(`Insufficient BroScore for BROMETHEUS upgrade (need ${upgradeCost})`);
        return { success: false };
      }

      console.log('👑 Processing BROMETHEUS upgrade (using legacy upgrade system)');
      const result = await upgradeToBrometheus(address, userData.bro_score ?? 0, upgradeCost);
      
      if (result.error) {
        toast.error(result.error);
        return { success: false };
      }

      toast.success('Congratulations! You are now BROMETHEUS!');
      return { success: true, brocode: result.brocode };
    } catch (error) {
      console.error('Error in BROMETHEUS upgrade:', error);
      toast.error('Failed to upgrade to BROMETHEUS');
      return { success: false };
    }
  };

  return {
    isLoading,
    handleCipherPath,
    handleSocialBroPath,
    handleReroll,
    handleConnectWallet,
    handleRerollWithXp,
    handleRerollWithTokens,
    handleRerollWithLiquidity,
    handleBrometheusUpgrade,
  };
};
